/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import isUndefined from 'lodash.isundefined';
import {
  AlertFired,
  AlertSeverity
} from '@ion/core/src/types/entities';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {
  Notification,
  NotificationAction,
  NotificationCategory
} from '@ion/dls/src/types/extensions';
import {NotificationPanelTabs} from '@ion/dls/src/types/types';
import {
  POST_INITIALIZATION_COMPLETE,
  POST_INITIALIZATION_ERROR,
  POST_INITIALIZATION_REQUEST
} from '@/store/actions/initialization';
import {
  ALERTS_REFRESH_UPDATED,
  DESELECT_ALL_NOTIFICATIONS,
  GET_NOTIFICATION_DETAILS_ERROR,
  GET_NOTIFICATION_DETAILS_REQUEST,
  GET_NOTIFICATION_DETAILS_RESPONSE,
  GET_NOTIFICATION_ERROR,
  GET_NOTIFICATION_REPONSE,
  GET_NOTIFICATION_REQUEST,
  MARK_AS_CLEARED_ERROR,
  MARK_AS_CLEARED_REQUEST,
  MARK_AS_CLEARED_RESPONSE,
  MARK_AS_READ_ERROR,
  MARK_AS_READ_REQUEST,
  MARK_AS_READ_RESPONSE,
  REMOVE_TOAST_NOTIFICATION,
  REMOVE_URGENT_ENABLED_NOTIFICATION,
  RESET_NOTIFICATION_ERROR,
  RESET_NOTIFICATION_REQUEST,
  RESET_NOTIFICATION_RESPONSE,
  SELECT_ALL_NOTIFICATIONS,
  SELECT_NOTIFICATION,
  SET_NOTIFICATION_PANEL_STATE,
  SET_NOTIFICATIONS_ACTIVE_PANEL,
  SET_NOTIFICATIONS_PANEL_OPEN
} from '@/store/actions/notifications';
import {AsyncState} from '@/types/redux';

/**
 * Defines the shape of the notifications state, which includes the following:
 *
 *  - all            : Provides a reference to all Notifications (both read and unread)
 *  - urgent         : Provides a reference to all urgent notifications.
 *  - latest         : Provides a reference to the latest retrieved Notifications via
 *                     the auto-refresh interval fetching of notifications.
 *  - unreadCount    : Specifies the number of unread notifications.
 *  - loading        : When true, indicates that the initial notifications are currently
 *                     being retrieved (i.e. all and urgent notifications are being set).
 *  - refreshing     : When true, indicates that the latest notifications are currently
 *                     being retrieved.
 *  - updating       : When true, indicates that a notification or multiple notifications
 *                     are currently being marked as read.
 *  - loadingDetails : When true, indicates that a notification or multiple notifications
 *                     are currently being marked as read.
 */
export interface NotificationsState extends AsyncState {
  all: Notification[] | null;
  urgent: Notification[] | null;
  latest: Notification[] | null;
  unreadCount: number;
  refreshing: boolean;
  updating: boolean;
  loadingDetails: boolean;
  panel: {
    activePanel: NotificationPanelTabs;
    open: boolean;
    state: PanelState[];
    selected: Notification[];
  };
}

export interface PanelState {
  id: string;
  open: boolean;
  loading: boolean;
}

// defines the initial state for the reducer ...
export const initialState: NotificationsState = {
  all            : [],
  urgent         : [],
  latest         : [],
  unreadCount    : 0,
  loading        : false,
  error          : null,
  refreshing     : false,
  updating       : false,
  loadingDetails : false,
  panel          : {
    activePanel : NotificationPanelTabs.urgent,
    open        : false,
    state       : [],
    selected    : []
  }
};

// defines the notifications reducers ...
const reducers = {
  [POST_INITIALIZATION_REQUEST] : (draft: NotificationsState) => {
    draft.loading = true;
  },
  [POST_INITIALIZATION_COMPLETE] : (draft: NotificationsState, {notifications}) => {
    setNotifications(draft, notifications);
  },
  [POST_INITIALIZATION_ERROR] : (draft: NotificationsState) => {
    draft.loading = false;
  },
  [GET_NOTIFICATION_REQUEST] : ({loading}: NotificationsState) => {
    loading = true;
  },
  [GET_NOTIFICATION_REPONSE] : (draft: NotificationsState, {notifications}) => {
    setNotifications(draft, notifications);
  },
  [GET_NOTIFICATION_ERROR] : ({loading}: NotificationsState) => {
    loading = false;
  },
  [SET_NOTIFICATIONS_PANEL_OPEN] : ({panel}: NotificationsState, {open}) => {
    panel.open = open;
  },
  [SET_NOTIFICATIONS_ACTIVE_PANEL] : ({panel}: NotificationsState, {activePanel}) => {
    panel.activePanel = activePanel;
  },
  [REMOVE_TOAST_NOTIFICATION] : (draft: NotificationsState, {index}) => {
    draft.latest.splice(index, 1);
  },
  [REMOVE_URGENT_ENABLED_NOTIFICATION] : (draft: NotificationsState, {uuid}) => {
    draft.latest = draft.latest?.filter(notification =>
      notification?.uuid !== uuid) || [];
  },
  [GET_NOTIFICATION_DETAILS_REQUEST] : (draft: NotificationsState) => {
    draft.loadingDetails = true;
  },
  [GET_NOTIFICATION_DETAILS_RESPONSE] : (draft: NotificationsState, {notification}
  ) => {
    updateNotification(draft.all, notification);
    if (notification.isUrgent) {
      updateNotification(draft.urgent, notification);
    }
    draft.loadingDetails = false;
    const {uuid} = notification;
    const existingState = [...draft.panel.state];
    const matchingIndex = existingState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      existingState[matchingIndex] = {id: uuid, open: true, loading: false};
      const updatedState = existingState;
      draft.panel.state = updatedState;
    } else {
      draft.panel.state = [...existingState, {id: uuid, open: true, loading: false}];
    }
  },
  [GET_NOTIFICATION_DETAILS_ERROR] : ({loadingDetails}: NotificationsState) => {
    loadingDetails = true;
  },
  [MARK_AS_READ_REQUEST] : (draft: NotificationsState) => {
    draft.updating = true;
  },
  [MARK_AS_READ_RESPONSE] : (draft: NotificationsState, {notifications}) => {
    draft.unreadCount = getUnreadCount(draft, notifications);
    draft.all = updateNotifications(draft.all, notifications, notification => {
      return {...notification, isAcknowledged: true, isRead: true};
    });
    draft.urgent = updateNotifications(draft.urgent, notifications, notification => {
      return {...notification, isAcknowledged: true, isRead: true};
    });
    draft.updating = false;
  },
  [MARK_AS_READ_ERROR] : (draft: NotificationsState) => {
    draft.updating = false;
  },
  [MARK_AS_CLEARED_REQUEST] : ({updating}: NotificationsState) => {
    updating = true;
  },
  [MARK_AS_CLEARED_RESPONSE] : (draft: NotificationsState, {notifications}) => {
    const ids = idMap(notifications);
    const filter = ({id}) => !ids[id];
    draft.all = draft.all?.filter(filter);
    draft.urgent = draft.urgent?.filter(filter);
    draft.unreadCount = getUnreadCount(draft, notifications);
    draft.updating = false;
  },
  [MARK_AS_CLEARED_ERROR] : ({updating}: NotificationsState) => {
    updating = false;
  },
  [ALERTS_REFRESH_UPDATED] : (draft: NotificationsState, {latest, urgentNotificationsEnabled}) => {
    const {all, urgent, unreadCount} = normalize(latest);
    draft.all = all.concat(draft.all);
    draft.urgent = urgent.concat(draft.urgent);
    draft.unreadCount += unreadCount;
    draft.latest = urgentNotificationsEnabled && draft.latest?.length
      ? latest.concat(draft.latest)
      : latest;
  },
  [SET_NOTIFICATION_PANEL_STATE] : ({panel}: NotificationsState, state) => {
    panel.state = [...state.newState];
  },
  [SELECT_NOTIFICATION] : (draft: NotificationsState, {selectedNotifications, activePanel}) => {
    draft.panel.selected = [...selectedNotifications];
  },
  [SELECT_ALL_NOTIFICATIONS] : (draft: NotificationsState, {notifications, activePanel}) => {
    draft.panel.selected = [...notifications.all, ...notifications.urgent];
  },
  [DESELECT_ALL_NOTIFICATIONS] : (draft: NotificationsState, {notifications}) => {
    draft.panel.selected = [];
  },
  [RESET_NOTIFICATION_REQUEST] : (draft: NotificationsState) => {
    draft.loading = true;
  },
  [RESET_NOTIFICATION_RESPONSE] : (draft: NotificationsState, {notifications}) => {
    setNotifications(draft, notifications);
  },
  [RESET_NOTIFICATION_ERROR] : (draft: NotificationsState) => {
    draft.loading = false;
  }
};

/**
 * Provides a utility which, given a list of AlertFired entities, converts them
 * to a collection of Notification types suitable for use within iOn Suite.
 */
export const normalize = (
  alerts: AlertFired[]
): { all: Notification[], urgent: Notification[], unreadCount: number } => {
  const all = [];
  const urgent = [];
  let unreadCount = 0;
  alerts.forEach((alert: AlertFired) => {
    const category = categoryMap[alert.severity];
    const isUrgent = category === NotificationCategory.Urgent;
    const isRead = alert.isAcknowledged === true;
    const notification = {
      action    : isRead ? NotificationAction.Read : null,
      isCleared : false,
      isUrgent,
      isRead,
      category,
      ...alert
    };
    !isRead && unreadCount++;
    isUrgent && urgent.push(notification);
    all.push(notification);
  });
  return {all, urgent, unreadCount};
};

export const categoryMap = {
  [AlertSeverity.CRITICAL] : NotificationCategory.Urgent,
  [AlertSeverity.HIGH]     : NotificationCategory.General,
  [AlertSeverity.MEDIUM]   : NotificationCategory.TimeRelated,
  [AlertSeverity.LOW]      : NotificationCategory.ConnectivitySignal,
  [AlertSeverity.SYSTEM]   : NotificationCategory.ScheduledEvents
};

export const getUnreadCount = ({all, unreadCount}: NotificationsState,
  notifications: Notification[]): number => {
  let unread = 0;
  notifications.forEach(({id, isRead}) => {
    if (!isUndefined(isRead) && !isRead) {
      unread++;
    } else {
      unread += all.filter(item => item.id === id && !item.isRead)?.length;
    }
  });
  return unreadCount - unread;
};

// provides a utility which updates a notification within a collection of notifications ...
const updateNotification = (items: Notification[], notification: Notification) => {
  const index = items.findIndex(({id}) => notification.id === id);
  if (index !== -1) {
    items[index] = notification;
  }
};

// provides a utility which updates a list of notifications notification within a collection
// of notifications ...
export const updateNotifications = (
  items: Notification[], updated: Notification[], updater: (item: Notification) => Notification
): Notification[] => {
  const ids = idMap(updated);
  return items.map(notification => {
    const item = ids[notification.id];
    if (item) {
      return updater(item);
    }
    return notification;
  });
};

// provides a utility that normalizes the notification API response into the required format
// for the application to set the different states.
export const setNotifications = (draft: NotificationsState, notifications: Notification[]) => {
  const {all, urgent, unreadCount} = normalize(notifications);
  draft.all                        = all;
  draft.urgent                     = urgent;
  draft.unreadCount                = unreadCount;
  draft.loading                    = false;
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: NotificationsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
